import {TypedEventEmitter}  from "./events";

export interface DataSourceOptions {
    data?: DataSourceData,
    serverFiltering?: boolean,
    serverFilteringService?: string,
    services?: Array<DataSourceService>
}

interface RequestEndEvent extends CustomEvent<{ data: DataSourceData }> { }
interface ChangeEvent extends CustomEvent<{ data: DataSourceData | null }> { }
interface FilterEvent extends CustomEvent<{ filters: Array<DataSourceFilter> }> { }

/** Class to manage DataSources */
export default class DataSource {

    private _filters: Array<DataSourceFilter> = []
    private _data: DataSourceData | null;


    // onerror: EventEmitter<Error> = new EventEmitter("error");

    readonly events = {
        // /** Event fired when error occur */
        // error: new EventEmitter<DataSourceEvent<Error>>(),

        /** Event fired on request ending, right before "change" event, where the "data" argument can be modified. */
        requestEnd: new TypedEventEmitter<RequestEndEvent>(),

        /** Event fired when data has changed. Can be after a request or filter operation */
        change: new TypedEventEmitter<ChangeEvent>(),

        /** Event fired when filter is call */
        filter: new TypedEventEmitter<FilterEvent>(),
    }

    /** Shortcuts to invoke services */
    //readonly services: { [key: string]: (value?: any) => Promise<DataSourceData> } = {}
    readonly services: Record<string, (value?: any) => Promise<DataSourceData>> = {}

    readonly options: DataSourceOptions;

    constructor(options: DataSourceOptions) {
        this.options = options;
        this._data = options.data || null;

        options.services?.forEach(service => {
            this.createServiceShortcut(service)
        });
    }

    get filters() {
        return this._filters;
    }

    get filterText() {
        return this._filters.length > 0 ? this._filters[0].value : ""
    }

    addService(service: DataSourceService): void {
        if (!this.options.services) {
            this.options.services = []
        }

        const previousService = this.options.services.find(svc => svc.name == service.name)

        if (previousService) {
            const index = this.options.services.indexOf(previousService)
            this.options.services.splice(index, 1)
        }

        this.options.services.push(service)
        this.createServiceShortcut(service)
    }

    /** Apply a filter on current or future data.
     * @param filter Field name or a filter object.
     * @param value Value of filter for use with "Contains" operator. Used only when "filter" is a field name.
     */
    filter(filter: DataSourceFilter | string, value?: string): void {
        if (typeof filter === 'string') {
            this._filters = [
                { logic: LogicOperator.And, field: filter, operator: ComparisonOperator.Contains, value: value || '', ignoreCase: true }
            ]
        } else {
            this._filters = [filter];
        }

        if (this.options.serverFiltering === true) {
            if (this.options.serverFilteringService) this.invoke(this.options.serverFilteringService)
            return;
        }

        const dataWithFilter = this.applyFilters(this._data);
        this.onFilter(dataWithFilter)
        this.onChange(dataWithFilter);
    }

    /** Get current data
     * @param ignoreFilters If true, apply filters before return
     */
    getData(ignoreFilters: boolean = true): DataSourceData {
        return ignoreFilters
            ? this._data
            : this.applyFilters(this._data);
    }

    /** Set static data 
     * @param data Static data
    */
    setData(data: DataSource): void {
        this._data = data;
    }

    /** Create shortcut methods to invoke services. The service name will be the method name in "services" properties. 
     * @example dataSource.services.serviceName(value) */
    private createServiceShortcut(service: DataSourceService): void {
        if (!this.services[service.name]) {
            this.services[service.name] = function (that: DataSource, serviceName: string) {
                return function (value?: any): Promise<any> {
                    return that.invoke(serviceName, value)
                }
            }(this, service.name)
        }
    }

    /** Apply current filters on "data".
     * @param data Data to be filtered
     */
    private applyFilters(data: DataSourceData | null): DataSourceData | null {

        if (!data || !this._filters || this._filters.length === 0)
            return data

        const dataArray = Array.isArray(data) ? data : [data]
        const dataArrayFiltered: Array<object> = []

        dataArray.forEach((dataItem: any) => {
            let isTrue = true
            let isTrueItem = false;

            this._filters.forEach(filter => {
                let fieldValue: any = dataItem[filter.field];

                switch (filter.operator) {
                    case ComparisonOperator.Equals:
                        isTrueItem = filter.ignoreCase === true
                            ? fieldValue?.toString()?.toUpperCase() === filter.value?.toString()?.toUpperCase()
                            : fieldValue == filter.value;
                        break;

                    case ComparisonOperator.Contains:
                        isTrueItem = filter.ignoreCase === true
                            ? fieldValue?.toString()?.toUpperCase()?.indexOf(filter.value?.toString()?.toUpperCase()) >= 0
                            : fieldValue?.toString().indexOf(filter.value) >= 0;
                        break;

                    default:
                        throw new Error(`Operator "${filter.operator}" not implemented.`);
                }

                if (filter.logic === LogicOperator.And) {
                    isTrue = isTrue && isTrueItem;
                }
                else if (filter.logic === LogicOperator.Or) {
                    isTrue = isTrue || isTrueItem;
                }
            })

            if (isTrue) dataArrayFiltered.push(dataItem)
        })


        if (Array.isArray(data)) return dataArrayFiltered
        else if (dataArrayFiltered.length === 0) return null
        else return dataArrayFiltered[0]
    }

    /** Invoke a remote service. 
     * @param serviceName Service name.
     * @param requestData Data to be send to the service.
    */
    invoke(serviceName: string, requestData?: any): Promise<DataSourceData> {

        return new Promise<DataSourceData>((resolve, reject) => {
            const service = this.options?.services?.find(svc => svc.name == serviceName);

            if (!service) {
                throw new Error(`Service "${service}" not specified.`)
            }


            service.fetch(this, requestData)
                .then(resp => {
                    this.fetchCallback(resp)
                    resolve(resp);
                })
        })
    }


    /** Default callback for "invoke" method */
    private fetchCallback(responseData: DataSourceData): void {
        // requestEnd pode modificar o resultado
        let data: DataSourceData = this.onRequestEnd(responseData);

        this._data = data;

        if (!this.options.serverFiltering)
            data = this.applyFilters(data);

        this.onChange(data);
    }

    // private onError(error: Error): void {
    //     this.events.error?.emit(new DataSourceEvent(this, "error", error));
    // }

    private onFilter(data: DataSourceData | null): void {
        const event = new CustomEvent("filter", { detail: { filters: this._filters } });
        this.events.filter?.emit(event);
    }

    private onChange(data: DataSourceData | null): void {
        const event = new CustomEvent("change", { detail: { data } });
        this.events.change.emit(event);
    }

    private onRequestEnd(data: DataSourceData): DataSourceData {
        const event = new CustomEvent("requestEnd", { detail: { data } });
        this.events.requestEnd?.emit(event);
        return event.detail.data;
    }
}

export type DataSourceServiceMapArgs = { argument: any, filterText: string };
export type DataSourceServiceMapDelegate = (args: DataSourceServiceMapArgs) => Record<string, any>

export interface DataSourceService {
    name: string,
    fetch(dataSource: DataSource, argument?: any): Promise<DataSourceData>
}
export type DataSourceData = object | string | null;

/************************ Not Exported  ********************** */

enum LogicOperator {
    And, Or
}

enum ComparisonOperator {
    Contains,
    Equals
}

type DataSourceFilter = {
    logic: LogicOperator,
    field: string,
    operator: ComparisonOperator,
    value: string,
    ignoreCase: boolean
}


// class DataSourceEvent<TDetail = any> implements EventData {
//     constructor(
//         public sender: DataSource,
//         public readonly type: string,
//         public readonly detail: TDetail) { }
// }

